import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';

/**
 * React dom wrapper
 * renders children into dom element
 * @param {object} containerSelector Element selector to match location
 * where container element is created
 * @param {string} nodeSelector Element placeholder where ReactDOM renders
 * @param {string} nodeClass Element placeholder class name
 * @param {object} children
 * @param {array} updatableProps contains prop names that should trigger rerender on child elements on update
 * */
class DomWrapper extends PureComponent {
  static displayName = 'DomWrapper';

  static propTypes = {
    nodeSelector: PropTypes.string,
    nodeClass: PropTypes.string.isRequired,
    containerSelector: PropTypes.string.isRequired,
  };

  static defaultProps = {
    nodeSelector: 'div',
  };

  constructor(props) {
    super(props);

    this.readyListener = this.readyListener.bind(this);
  }

  componentDidMount() {
    this.readyListener();
  }

  componentDidUpdate(prevProps) {
    const { updatableProps } = this.props;
    const isNeedRerender =
      updatableProps &&
      updatableProps.some((prop) => this.props[prop] !== prevProps[prop]);
    if (isNeedRerender) {
      this.removeElementFromDOM();
      this.readyListener();
    }
  }

  componentWillUnmount() {
    this.removeElementFromDOM();
  }

  removeElementFromDOM() {
    const { nodeClass, innerRef } = this.props;
    const element = nodeClass
      ? document.querySelector(`.${nodeClass.replace(/ /g, '.')}`)
      : innerRef;
    if (element) {
      ReactDOM.unmountComponentAtNode(element);
      element.parentElement.removeChild(element);
    }
  }

  /**
   * Callback used by Learnosity API to notify siblings
   * that the API is ready
   * */
  readyListener() {
    const {
      containerSelector,
      nodeSelector,
      nodeClass,
      children,
      innerRef,
      playerContainerId,
      ...otherProps
    } = this.props;
    const playerContainer = playerContainerId
      ? document.querySelector(`#${playerContainerId}`)
      : document;
    const containerElement = playerContainer.querySelector(containerSelector);
    const containerNode = document.createElement(nodeSelector);
    containerNode.className = nodeClass;

    containerElement.insertBefore(containerNode, containerElement.firstChild);
    const childrenElements = React.Children.toArray(children);
    ReactDOM.render(
      childrenElements.map((child, index) =>
        React.cloneElement(child, {
          ...otherProps,
          key: `dom-wrapper-${index}`,
          ref: innerRef,
        }),
      ),
      containerNode,
    );
  }

  render() {
    return null;
  }
}

export default React.forwardRef((props, ref) => (
  <DomWrapper innerRef={ref} {...props} />
));
